interface ErrorContext {
  userId?: string | number;
  page?: string;
  componentStack?: string;
  userAgent?: string;
  url?: string;
  additionalInfo?: Record<string, any>;
}

interface DiscordEmbed {
  title: string;
  description: string;
  color: number;
  fields?: Array<{
    name: string;
    value: string;
    inline?: boolean;
  }>;
  timestamp?: string;
  footer?: {
    text: string;
  };
}

class DiscordLogger {
  private webhookUrl: string | undefined;
  private environment: string;
  private appName: string;
  private enabled: boolean;

  constructor() {
    this.webhookUrl = process.env.REACT_APP_DISCORD_WEBHOOK_URL;
    this.environment = process.env.NODE_ENV || 'development';
    this.appName = 'PrEPStop Frontend';
    this.enabled = !!this.webhookUrl;

    if (!this.enabled && this.environment === 'production') {
      console.warn('⚠️  Discord webhook URL not configured for frontend.');
    }
  }

  /**
   * Log frontend error to Discord
   */
  async logError(error: Error, context?: ErrorContext): Promise<void> {
    if (!this.enabled || !this.webhookUrl) {
      return;
    }

    try {
      const embed: DiscordEmbed = {
        title: `🌐 ${this.environment.toUpperCase()} Frontend Error: ${error.name}`,
        description: this.truncate(error.message, 2000),
        color: this.getColorByEnvironment(),
        fields: [],
        timestamp: new Date().toISOString(),
        footer: {
          text: `${this.appName} | ${this.environment}`
        }
      };

      // Add context fields
      if (context?.page) {
        embed.fields!.push({
          name: '📄 Page',
          value: context.page,
          inline: true
        });
      }

      if (context?.url) {
        embed.fields!.push({
          name: '🔗 URL',
          value: context.url,
          inline: true
        });
      }

      if (context?.userId) {
        embed.fields!.push({
          name: '👤 User ID',
          value: String(context.userId),
          inline: true
        });
      }

      if (context?.userAgent) {
        embed.fields!.push({
          name: '🖥️ User Agent',
          value: this.truncate(context.userAgent, 200),
          inline: false
        });
      }

      // Stack trace
      if (error.stack) {
        embed.fields!.push({
          name: '📋 Stack Trace',
          value: this.formatStackTrace(error.stack),
          inline: false
        });
      }

      // Component stack (React)
      if (context?.componentStack) {
        embed.fields!.push({
          name: '⚛️ Component Stack',
          value: this.formatStackTrace(context.componentStack),
          inline: false
        });
      }

      // Additional info
      if (context?.additionalInfo && Object.keys(context.additionalInfo).length > 0) {
        const infoStr = JSON.stringify(context.additionalInfo, null, 2);
        embed.fields!.push({
          name: 'ℹ️ Additional Info',
          value: this.truncate(infoStr, 1000),
          inline: false
        });
      }

      const payload = {
        username: this.appName,
        embeds: [embed]
      };

      await fetch(this.webhookUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
    } catch (discordError) {
      console.error('Failed to send error to Discord:', discordError);
    }
  }

  /**
   * Log unhandled promise rejection
   */
  async logUnhandledRejection(reason: any, promise: Promise<any>): Promise<void> {
    const error = reason instanceof Error ? reason : new Error(String(reason));
    
    await this.logError(error, {
      page: window.location.pathname,
      url: window.location.href,
      userAgent: navigator.userAgent,
      additionalInfo: {
        type: 'Unhandled Promise Rejection',
        promise: String(promise)
      }
    });
  }

  /**
   * Get embed color based on environment
   */
  private getColorByEnvironment(): number {
    switch (this.environment) {
      case 'production':
        return 0xFF0000; // Red
      case 'staging':
        return 0xFFA500; // Orange
      case 'development':
      default:
        return 0xFFFF00; // Yellow
    }
  }

  /**
   * Format stack trace for Discord
   */
  private formatStackTrace(stack: string): string {
    const lines = stack.split('\n').slice(0, 10);
    const formatted = lines.map(line => line.trim()).join('\n');
    return '```\n' + this.truncate(formatted, 900) + '\n```';
  }

  /**
   * Truncate string to max length
   */
  private truncate(str: string, maxLength: number): string {
    if (str.length <= maxLength) {
      return str;
    }
    return str.substring(0, maxLength - 3) + '...';
  }
}

// Export singleton instance
export const discordLogger = new DiscordLogger();

// Setup global error handlers
export const setupGlobalErrorHandlers = () => {
  // Catch unhandled errors
  window.addEventListener('error', (event) => {
    discordLogger.logError(event.error || new Error(event.message), {
      page: window.location.pathname,
      url: window.location.href,
      userAgent: navigator.userAgent,
      additionalInfo: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno
      }
    });
  });

  // Catch unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    discordLogger.logUnhandledRejection(event.reason, event.promise);
  });
};
